import { Modal as AntModal } from 'antd';
import { SAME_DAY_BOOKING_ALERT_INDEX } from 'constants/variables';
import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import { ISameDayAppointmentRow } from './same-day-appointments';

type Ref = {
  /** Resolves true on CONTINUE, false on CANCEL (or when a newer prompt replaces this one). */
  confirm: (rows: ISameDayAppointmentRow[], customerName?: string) => Promise<boolean>;
};

export const sameDayBookingAlertRef = React.createRef<Ref>();

// Warns that the customer already has an appointment on the day being booked.
const SameDayBookingAlert = forwardRef<Ref>((_, ref) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ISameDayAppointmentRow[]>([]);
  const [customerName, setCustomerName] = useState('');
  const resolveRef = useRef<((ok: boolean) => void) | null>(null);

  const settle = (ok: boolean) => {
    resolveRef.current?.(ok);
    resolveRef.current = null;
    setOpen(false);
  };

  useImperativeHandle(ref, () => ({
    confirm: (_rows, _customerName) => new Promise<boolean>(resolve => {
      resolveRef.current?.(false);
      resolveRef.current = resolve;
      setRows(_rows);
      setCustomerName(_customerName?.trim() || '');
      setOpen(true);
    }),
  }));

  return (
    <AntModal
      centered
      open={open}
      title={null}
      footer={null}
      closable={false}
      maskClosable={false}
      width={760}
      zIndex={SAME_DAY_BOOKING_ALERT_INDEX}
      onCancel={() => settle(false)}
      styles={{ content: { padding: 0, borderRadius: 32, overflow: 'visible' } }}
    >
      <Container>
        <div className='bell'><BellIcon /></div>
        <div className='title'>Customer Already Has a Booking</div>
        <div className='subtitle'>
          {customerName ? <><b>{customerName}</b> already has</> : 'This customer already has'} an appointment on this day.
        </div>
        <div className='section-label'>Details of the current appointment{rows.length > 1 ? 's' : ''}:</div>
        <div className='list'>
          {rows.map(o => (
            <div className='apt' key={o.id}>
              <div className='when'>
                <span className='cell'><CalendarIcon />{o.date}</span>
                <span className='divider' />
                <span className='cell'><ClockIcon />{o.timeRange}</span>
              </div>
              <div className='what'>
                <span className='staff'>{o.staffName}:</span>
                <span className='services'>{o.serviceNames}</span>
              </div>
            </div>
          ))}
        </div>
        <div className='warning'>If you proceed, this customer will have another booking on the same day.</div>
        <div className='actions'>
          <button type='button' className='btn btn-cancel' onClick={() => settle(false)}>CANCEL</button>
          <button type='button' className='btn btn-continue' onClick={() => settle(true)}>CONTINUE</button>
        </div>
      </Container>
    </AntModal>
  );
});
SameDayBookingAlert.displayName = 'SameDayBookingAlert';
export default SameDayBookingAlert;

const BellIcon = () => (
  <svg width='72' height='72' viewBox='0 0 24 24' fill='none' stroke='#E5493A' strokeWidth='1.6' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9' />
    <path d='M10.3 21a1.94 1.94 0 0 0 3.4 0' />
    <path d='M2.5 6.5a8 8 0 0 1 2-3.5M21.5 6.5a8 8 0 0 0-2-3.5' />
  </svg>
);
const CalendarIcon = () => (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='#1D2129' strokeWidth='1.6' strokeLinecap='round'>
    <rect x='3' y='4.5' width='18' height='16' rx='2' /><path d='M3 9.5h18M8 2.5v4M16 2.5v4' />
  </svg>
);
const ClockIcon = () => (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='#1D2129' strokeWidth='1.6' strokeLinecap='round'>
    <circle cx='12' cy='12' r='9' /><path d='M12 7v5l3 2' />
  </svg>
);

const Container = styled.div`
  position: relative;
  padding: 72px 32px 28px;
  font-family: Poppins, sans-serif;
  color: #1D2129;
  text-align: center;

  .bell {
    position: absolute;
    top: -68px;
    left: 50%;
    transform: translateX(-50%);
    width: 136px;
    height: 136px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .title { font-size: 26px; font-weight: 600; }
  .subtitle { margin-top: 6px; font-size: 16px; color: #505050; }
  .section-label { margin-top: 20px; font-size: 18px; }
  .list {
    margin-top: 12px;
    max-height: 50vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-right: 4px;
    scrollbar-width: thin;
    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-thumb { background: #CECECE; border-radius: 3px; }
  }
  /* A scrolling flex column would otherwise squeeze the cards until their content overlaps. */
  .apt { flex-shrink: 0; background: #F6F7FC; border-radius: 8px; padding: 10px 12px; }
  .when { display: flex; align-items: center; gap: 12px; font-size: 18px; }
  .cell { display: flex; align-items: center; gap: 8px; }
  .divider { width: 1px; align-self: stretch; background: #CECECE; }
  .what {
    margin-top: 8px;
    padding: 10px 14px;
    background: #fff;
    border-radius: 6px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 17px;
    text-align: left;
    .services { text-align: right; }
  }
  .warning { margin: 16px 0 20px; font-size: 17px; color: #F08A24; }
  .actions {
    border-top: 1px solid #E5E6EB;
    padding-top: 20px;
    display: flex;
    gap: 16px;
  }
  .btn {
    flex: 1;
    height: 64px;
    border-radius: 10px;
    font-size: 20px;
    font-weight: 600;
  }
  .btn-cancel { background: #E5E6EB; color: #1D2129; }
  .btn-continue { background: #F05326; color: #fff; }
`;
